import { lazy, Suspense } from "react";
import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/lib/theme-context";
import { useAnalytics } from "@/hooks/use-analytics";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileCallButton from "@/components/MobileCallButton";

// Home is eager-loaded — it's the most common landing page and must render immediately
import Home from "@/pages/Home";
// NotFound is tiny and needed on any route miss
import NotFound from "@/pages/not-found";

// Core pages — lazy-loaded; visited from nav after initial paint
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));

// Service pages — lazy-loaded
const ACHeatingInstallation = lazy(() => import("@/pages/ACHeatingInstallation"));
const ACHeatingRepair = lazy(() => import("@/pages/ACHeatingRepair"));
const DuctlessMiniSplits = lazy(() => import("@/pages/DuctlessMiniSplits"));
const MaintenancePlans = lazy(() => import("@/pages/MaintenancePlans"));
const SolarACPage = lazy(() => import("@/pages/SolarACPage"));
const CommercialHVAC = lazy(() => import("@/pages/CommercialHVAC"));

// Content pages — lazy-loaded
const Blog = lazy(() => import("@/pages/Blog"));
const BlogArticle = lazy(() => import("@/pages/BlogArticle"));
const Financing = lazy(() => import("@/pages/Financing"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("@/pages/TermsOfUse"));

// Legacy solar specialty pages — lazy-loaded (rarely visited, large files)
const SolarPoweredMiniSplit = lazy(() => import("@/pages/SolarPoweredMiniSplit"));
const CeilingCassetteMiniSplit = lazy(() => import("@/pages/CeilingCassetteMiniSplit"));
const DuctedSolarAirConditioner = lazy(() => import("@/pages/DuctedSolarAirConditioner"));
const SolarACFoodTrucks = lazy(() => import("@/pages/SolarACFoodTrucks"));
const OffGridCabinAC = lazy(() => import("@/pages/OffGridCabinAC"));
const SolarRVConversions = lazy(() => import("@/pages/SolarRVConversions"));
const TinyHomeCeilingCassette = lazy(() => import("@/pages/TinyHomeCeilingCassette"));
const SolarHybridADU = lazy(() => import("@/pages/SolarHybridADU"));
const SolarACWorkshops = lazy(() => import("@/pages/SolarACWorkshops"));

/** Minimal full-height placeholder shown while a lazy chunk is loading */
function PageSkeleton() {
  return <div className="min-h-screen" aria-hidden="true" />;
}

function Router() {
  return (
    <Suspense fallback={<PageSkeleton />}>
      <Switch>
        {/* ── Core ─────────────────────────────────────────────── */}
        <Route path="/" component={Home} />
        <Route path="/about" component={About} />
        <Route path="/contact" component={Contact} />

        {/* ── Service pages ────────────────────────────────────── */}
        <Route path="/ac-heating-installation" component={ACHeatingInstallation} />
        <Route path="/ac-heating-repair" component={ACHeatingRepair} />
        <Route path="/ductless-mini-splits" component={DuctlessMiniSplits} />
        <Route path="/maintenance-plans" component={MaintenancePlans} />
        <Route path="/solar-powered-ac" component={SolarACPage} />
        <Route path="/commercial-hvac" component={CommercialHVAC} />

        {/* ── Content ──────────────────────────────────────────── */}
        <Route path="/hvac-resources" component={Blog} />
        <Route path="/hvac-resources/:id" component={BlogArticle} />
        <Route path="/financing" component={Financing} />
        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path="/terms-of-use" component={TermsOfUse} />

        {/* ── Legacy solar product pages → redirect to solar service page ─ */}
        <Route path="/solar-powered-mini-split">
          <Redirect to="/solar-powered-ac#mini-split" />
        </Route>
        <Route path="/ceiling-cassette-mini-split">
          <Redirect to="/solar-powered-ac#mini-split" />
        </Route>
        <Route path="/ducted-solar-air-conditioner">
          <Redirect to="/solar-powered-ac#ducted" />
        </Route>

        {/* ── Legacy specialty solar pages (kept for now) ─────── */}
        <Route path="/solar-ac-for-food-trucks" component={SolarACFoodTrucks} />
        <Route path="/off-grid-cabin-air-conditioning" component={OffGridCabinAC} />
        <Route path="/solar-power-for-rv-conversions" component={SolarRVConversions} />
        <Route path="/tiny-home-ceiling-cassette-ac" component={TinyHomeCeilingCassette} />
        <Route path="/solar-hybrid-adu-cooling" component={SolarHybridADU} />
        <Route path="/solar-ac-for-workshops-and-sheds" component={SolarACWorkshops} />

        {/* ── Legacy URL redirects ──────────────────────────────── */}
        <Route path="/solar-powered-air-conditioners">
          <Redirect to="/solar-powered-ac" />
        </Route>
        <Route path="/solar-powered-air-conditioners/:id">
          <Redirect to="/solar-powered-ac" />
        </Route>
        <Route path="/about-solar-powered-ac">
          <Redirect to="/about" />
        </Route>
        <Route path="/solar-ac-support">
          <Redirect to="/contact" />
        </Route>
        <Route path="/solar-ac-faq">
          <Redirect to="/solar-powered-ac" />
        </Route>
        <Route path="/solar-ac-resources">
          <Redirect to="/hvac-resources" />
        </Route>
        <Route path="/solar-ac-resources/:id">
          {(params) => <Redirect to={`/hvac-resources/${params.id}`} />}
        </Route>
        <Route path="/free-consultation">
          <Redirect to="/contact" />
        </Route>
        <Route path="/solar-ac-installation-guide">
          <Redirect to="/solar-powered-ac" />
        </Route>
        <Route path="/solar-ac-learning-center">
          <Redirect to="/solar-powered-ac" />
        </Route>
        <Route path="/learn-everything-about-photon-ac">
          <Redirect to="/solar-powered-ac" />
        </Route>
        <Route path="/solar-ac-phoenix-arizona">
          <Redirect to="/solar-powered-ac" />
        </Route>
        <Route path="/solar-ac/:city">
          <Redirect to="/solar-powered-ac" />
        </Route>
        <Route path="/return-policy">
          <Redirect to="/privacy-policy" />
        </Route>

        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  useAnalytics();
  return <>{children}</>;
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <TooltipProvider>
            <AnalyticsProvider>
              <div className="min-h-screen flex flex-col">
                <Header />
                <main className="flex-1">
                  <Router />
                </main>
                <Footer />
                <MobileCallButton />
              </div>
            </AnalyticsProvider>
            <Toaster />
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
